'use client';
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { AllCommunityModule, ModuleRegistry, ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

ModuleRegistry.registerModules([AllCommunityModule]);

interface IRow {
  make: string;
  model: string;
  price: number;
  electric: boolean;
}

const getWidthFromHeader = (header: string, min = 80, max = 400) => {
  const width = header.length * 10 + 24;
  const temp = Math.min(Math.max(width, min), max);
  return convertWidth(temp, 1440);
};

const convertWidth = (width: number, screen = 1440) => {
  const currentScreenWidth = window.innerWidth;
  const columnWidth = Math.round((currentScreenWidth / screen) * width);
  return columnWidth;
};

interface GridExampleProps {
  loading?: boolean;
  stData?: any;
  newColumns?: any[];
  listData?: any[];
  onChange?: (data: any, type: string) => void;
}

const GridExample: React.FC<GridExampleProps> = ({
  loading = true,
  stData = {},
  newColumns = [],
  listData = [],
  onChange = () => {},
}) => {
  // Row data tính toán qua useMemo để tránh render lại nhiều lần
  const rowData = useMemo(() => listData ? [...listData] : [], [listData]);

  // ColDefs tính toán qua useMemo để tránh render lại nhiều lần
  const colDefs = useMemo(() => {
    if (Array.isArray(stData.col)) {
      return stData.col.map((col: any) => {
        const header = col.headerName || col.field || "";
        if (col.width === 100 && header) {
          return {
            ...col,
            colId: col.colId + '',
            width: getWidthFromHeader(header),
          };
        } else {
          return {
            ...col,
            colId: col.colId + '',
            width: convertWidth(col.width || 100, stData.screen),
          };
        }
      });
    }
    return [];
  }, [stData.col, stData.screen]);

  // Tính toán rowHeight & selectionColWidth
  const [rowHeight, setRowHeight] = useState(40);
  const [selectionColWidth, setSelectionColWidth] = useState(40);

  useEffect(() => {
    const handleResize = () => {
      const baseFactor = (48 * 100) / 1440 / 100;
      const factor = window.innerWidth > 1440 ? baseFactor * 0.9 : baseFactor;
      const newHeight = Math.round(window.innerWidth * factor);
      setRowHeight(newHeight);
      setSelectionColWidth(newHeight);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // onChange callback tối ưu hóa
  const handleColumnChanged = useCallback((e: any) => {
    const columnStatex = e.api.getColumnDefs();
    onChange(columnStatex, 'colChange');
  }, [onChange]);

  const handleRowSelected = useCallback((e: any) => {
    if (!stData?.hideRowsSelect) {
      const selectedRows = e.api.getSelectedRows();
      const ids = selectedRows.map((item: any) => item.warehouse_id_origin);
      onChange(ids, 'onSelectedRows');
    }
  }, [onChange, stData?.hideRowsSelect]);

  return (
    <div className={`ag-theme-alpine custom-table-ag${stData.hideRowsSelect ? '' : ' has-row-select'}`} style={{ width: "100%" }}>
      <AgGridReact
        domLayout="autoHeight"
        rowData={rowData}
        columnDefs={colDefs}
        rowHeight={rowHeight}
        loading={loading}
        rowSelection={!stData?.hideRowsSelect ? { mode: 'multiRow' } : undefined}
        selectionColumnDef={!stData?.hideRowsSelect ? { width: selectionColWidth } : undefined}
        onColumnResized={handleColumnChanged}
        onColumnMoved={handleColumnChanged}
        onRowSelected={handleRowSelected}
      />
    </div>
  );
};

export default GridExample;
